import { Box, Chip, CircularProgress, MenuItem, Select, Typography } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import { AddOrganizationModal } from './AddOrganizationModal'
import { BreadcrumbNav } from '../../customComponents/BreadcrumbNav'
import { Colors } from '../../../utils/colors'
import { Modal } from '../../customComponents/Modal'
import { OrganizationCardList } from './cards/OrganizationCardList'
import { UserInformationCard, type UserInformationFormValues } from './cards/UserInformationCard'
import { ActiveStatus, type SecurityLevel, type SelectableOption, type UserPermission } from '../../../utils/genericTypes'
import { capabilitiesForMembership, isUserAdministrator } from './cards/cardPermissions'
import { defaultSecurityLevelFor } from './cards/securityLevels'
import { handleApiResponse } from '../../../utils/apiHelpers'
import { useAuth } from '../../../hooks/use-auth'
import { useUnsavedChanges } from '../../../hooks/use-unsaved-changes'
import {
  GetBuildingOptionsForOrg,
  GetOrganizationOptionsForUser,
  GetUserDetails,
  SetDefaultOrganization,
  UpdateStatus,
  UpdateUser,
  UpdateUserBuildings,
  UploadProfileImage
} from '../../../services/UserManagementService'
import { GetAvailableOrganizations } from '../../../services/OrganizationService'
import {
  GetPermissionsForRole,
  GetUserFunctionsForOrg,
  RemoveAccessToOrganization,
  SaveUserFunctions
} from '../../../services/AuthorizedFunctionService'
import type { OrganizationCardChanges } from './cards/OrganizationCard'
import type { SelectableOrganization } from '../../../models/SelectableOrganization'
import type { User } from '../../../models/User'
import type { UserOrganization } from '../../../models/UserOrganization'

const layout = {
  page: { display: 'flex', flexDirection: 'column', gap: '1em' },
  heading: { display: 'flex', alignItems: 'center', gap: '3em' },
  panels: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 500px) minmax(0, 1fr)' },
    gap: '24px',
    alignItems: 'start'
  }
}

const activeStatusOptions = Object.values(ActiveStatus)

/**
 * AddUserModal routes to /User/:userId/:orgId after creating a user from an organization's list,
 * and that id is the only record of which organization the admin meant to add them to. It is
 * offered as an unsaved card rather than granted outright: the membership still needs a role, and
 * an admin who walks away here should leave no access behind.
 */
const pendingMembershipFor = (
  user: User | null,
  organizationsInScope: SelectableOrganization[] | null,
  orgId?: string
): UserOrganization[] => {
  const organization = organizationsInScope?.find((o) => o.id.toString() === orgId)
  if (organization == null) return []
  if ((user?.organizations ?? []).some((org) => org.id === organization.id)) return []

  return [draftMembership(organization)]
}

const draftMembership = (organization: SelectableOrganization): UserOrganization => ({
  id: organization.id,
  name: organization.name,
  organizationType: organization.type,
  roleName: defaultSecurityLevelFor(organization.type) as SecurityLevel,
  dateAdded: new Date(),
  isDefault: false,
  userFunctions: [],
  subscriptions: [],
  buildings: []
})

export function UserDetailsPage (): JSX.Element {
  const { userId, orgId } = useParams()
  const auth = useAuth()
  const unsaved = useUnsavedChanges()

  const [member, setMember] = useState<User | null>(null)
  const [memberOrgOptions, setMemberOrgOptions] = useState<SelectableOrganization[]>([])
  const [organizationsInScope, setOrganizationsInScope] = useState<SelectableOrganization[]>([])
  const [draftOrganizations, setDraftOrganizations] = useState<UserOrganization[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingOrg, setIsAddingOrg] = useState(false)
  const [isConfirmingInactive, setIsConfirmingInactive] = useState(false)
  const [orgPendingRemoval, setOrgPendingRemoval] = useState<UserOrganization | null>(null)

  const isSelf = auth.user != null && auth.user.id.toString() === userId
  const isAdmin = isUserAdministrator(auth.user)
  const organizations = [...(member?.organizations ?? []), ...draftOrganizations]
  const hasOrgOutsideScope = (member?.organizations ?? []).some((org) => !memberOrgOptions.some((o) => o.id === org.id))
  const addableOrganizations = organizationsInScope.filter((o) => !organizations.some((org) => org.id === o.id))
  const isInScope = (org: UserOrganization): boolean => organizationsInScope.some((o) => o.id === org.id)

  const loadUser = async (): Promise<User | null> => {
    const user = await handleApiResponse(GetUserDetails(userId ?? ''))
    if (user != null) setMember(user)
    return user
  }

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      setIsLoading(true)

      const user = await loadUser()

      const memberOrgs = await handleApiResponse(GetOrganizationOptionsForUser())
      if (memberOrgs != null) setMemberOrgOptions(memberOrgs)

      const scopedOrgs = await handleApiResponse(GetAvailableOrganizations())
      if (scopedOrgs != null) setOrganizationsInScope(scopedOrgs)

      setDraftOrganizations(pendingMembershipFor(user, scopedOrgs, orgId))

      setIsLoading(false)
    }

    void loadData()
  }, [userId, orgId])

  const reloadUser = async (): Promise<void> => {
    unsaved.clear()
    setDraftOrganizations([])
    setIsLoading(true)
    await loadUser()
    setIsLoading(false)
  }

  const reportErrors = (errors: string[]): void => {
    errors.forEach((error) => { toast.error(error) })
  }

  const handleSaveUserInformation = async (form: UserInformationFormValues): Promise<void> => {
    const { success, errors } = await UpdateUser(form)
    if (!success) {
      reportErrors(errors)
      return
    }

    toast.success('User saved successfully')
    await reloadUser()
  }

  const handleDefaultOrgClicked = async (organizationId: number): Promise<void> => {
    if (member == null) return

    await SetDefaultOrganization(member.id.toString(), organizationId)
    setMember({
      ...member,
      organizations: member.organizations.map((org) => ({ ...org, isDefault: org.id === organizationId }))
    })
    toast.success('Default organization updated')
  }

  const handleSaveOrganization = async (org: UserOrganization, changes: OrganizationCardChanges): Promise<void> => {
    if (member == null) return

    if (changes.hasAccess === false) {
      await removeAccess(org)
      return
    }

    if (changes.functions != null || changes.securityLevel != null) {
      const { success, errors } = await SaveUserFunctions(
        member.id,
        org.id,
        changes.securityLevel ?? org.roleName,
        changes.functions ?? []
      )
      if (!success) {
        reportErrors(errors)
        return
      }
    }

    if (changes.buildingIds != null) {
      await UpdateUserBuildings(member.id.toString(), org.id.toString(), changes.buildingIds)
    }

    toast.success('Organization saved')
    await reloadUser()
  }

  const removeAccess = async (org: UserOrganization): Promise<void> => {
    if (member == null) return

    const { success, errors } = await RemoveAccessToOrganization(member.id.toString(), org.id)
    if (!success) {
      reportErrors(errors)
      return
    }

    toast.success('Organization removed')
    await reloadUser()
  }

  const handleCancelOrganization = (org: UserOrganization): void => {
    setDraftOrganizations(draftOrganizations.filter((draft) => draft.id !== org.id))
    unsaved.setSectionDirty(`org-${org.id}`, false)
  }

  const handleAddOrganization = (organization: SelectableOrganization): void => {
    setIsAddingOrg(false)
    setDraftOrganizations([...draftOrganizations, draftMembership(organization)])
  }

  const handleStatusChange = async (status: ActiveStatus): Promise<void> => {
    if (status === ActiveStatus.Inactive) {
      setIsConfirmingInactive(true)
      return
    }

    await applyStatus(true)
  }

  const applyStatus = async (isActive: boolean): Promise<void> => {
    await UpdateStatus(userId ?? '', isActive)
    setIsConfirmingInactive(false)
    toast.success(`User ${isActive ? 'activated' : 'deactivated'}`)
    await reloadUser()
  }

  const loadBuildingOptions = async (orgId: number): Promise<SelectableOption[]> =>
    await handleApiResponse(GetBuildingOptionsForOrg(orgId)) ?? []

  const handleUploadImage = async (file: File): Promise<string | null> =>
    member == null ? null : await handleApiResponse(UploadProfileImage(member.id.toString(), file))

  const loadUserFunctions = async (orgId: number): Promise<string[] | null> =>
    member == null ? null : await handleApiResponse(GetUserFunctionsForOrg(member.id, orgId))

  const loadPermissionOptions = async (securityLevel: SecurityLevel): Promise<UserPermission[]> =>
    await handleApiResponse(GetPermissionsForRole(securityLevel)) ?? []

  if (isLoading || member == null) {
    return <CircularProgress />
  }

  return <>
    <Box data-testid='userDetailsPage' sx={layout.page}>
      <BreadcrumbNav onBeforeNavigate={unsaved.guard} />

      <Typography variant='h1' data-testid='pageHeader'>User Profile</Typography>

      <Box sx={layout.heading}>
        <Typography variant='h4' data-testid='nameDisplay'>{member.firstName} {member.lastName}</Typography>

        {!hasOrgOutsideScope && <Select
          value={member.isActive ? ActiveStatus.Active : ActiveStatus.Inactive}
          sx={{ fontSize: '.875em', height: '1.75em' }}
          inputProps={{ 'aria-label': 'Active Status' }}
          data-testid='activeStatusSelect'
          onChange={(e) => { void handleStatusChange(e.target.value as ActiveStatus) }}
          renderValue={(value) =>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FiberManualRecordIcon sx={{ color: value === ActiveStatus.Active ? Colors.ACTIVE_TEXT : Colors.INACTIVE_TEXT, fontSize: '1em', mr: '5px' }} />
              {value}
            </Box>
          }
        >
          {activeStatusOptions.map((status) => (
            <MenuItem key={status} value={status} data-testid={`statusMenuItem-${status}`}>{status}</MenuItem>
          ))}
        </Select>}

        {hasOrgOutsideScope && <Box data-testid='activeStatusDisplay'>
          <Chip label={
            <>
              <FiberManualRecordIcon sx={{ color: member.isActive ? Colors.ACTIVE_TEXT : Colors.INACTIVE_TEXT, fontSize: '1em', mr: '5px', verticalAlign: 'middle' }} />
              {member.isActive ? ActiveStatus.Active : ActiveStatus.Inactive}
            </>}
          />
        </Box>}
      </Box>

      <Box sx={layout.panels}>
        <UserInformationCard
          user={member}
          canEdit={isAdmin}
          allowEditEmail={!isSelf}
          onDirtyChange={(isDirty) => { unsaved.setSectionDirty('userInformation', isDirty) }}
          onSave={handleSaveUserInformation}
          onUploadImage={handleUploadImage}
        />

        <OrganizationCardList
          organizations={organizations}
          newOrganizationIds={draftOrganizations.map((org) => org.id)}
          canSetDefault={isAdmin}
          capabilitiesFor={(org, isNewMembership) => capabilitiesForMembership(isSelf, isAdmin && isInScope(org), isNewMembership)}
          editsInline={true}
          onSetDefault={handleDefaultOrgClicked}
          onSave={handleSaveOrganization}
          onCancel={handleCancelOrganization}
          onRemove={setOrgPendingRemoval}
          onDirtyChange={(orgId, isDirty) => { unsaved.setSectionDirty(`org-${orgId}`, isDirty) }}
          onAddOrganization={isAdmin ? () => { setIsAddingOrg(true) } : undefined}
          loadBuildingOptions={loadBuildingOptions}
          loadUserFunctions={loadUserFunctions}
          loadPermissionOptions={loadPermissionOptions}
        />
      </Box>
    </Box>

    {isAddingOrg && <AddOrganizationModal
      options={addableOrganizations}
      onClose={() => { setIsAddingOrg(false) }}
      onConfirm={handleAddOrganization}
    />}

    {orgPendingRemoval != null && <Modal
      open={true}
      title='Remove Organization'
      bodyContent={`Are you sure you want to remove the access for this user in ${orgPendingRemoval.name}?`}
      cancelButtonText='No, Cancel'
      confirmButtonText='Yes, Continue'
      onClose={() => { setOrgPendingRemoval(null) }}
      onCancel={() => { setOrgPendingRemoval(null) }}
      onConfirm={() => {
        const org = orgPendingRemoval
        setOrgPendingRemoval(null)
        void removeAccess(org)
      }}
    />}

    {isConfirmingInactive && <Modal
      open={true}
      title='Deactivate User'
      bodyContent='This will prevent this user from accessing the system. Do you want to continue?'
      cancelButtonText='No, Cancel'
      confirmButtonText='Yes, Continue'
      onClose={() => { setIsConfirmingInactive(false) }}
      onCancel={() => { setIsConfirmingInactive(false) }}
      onConfirm={() => { void applyStatus(false) }}
    />}

    {unsaved.pendingAction != null && <Modal
      open={true}
      title='Unsaved Changes'
      bodyContent='You have unsaved changes on this page. Leaving will discard them. Do you want to continue?'
      cancelButtonText='No, Stay'
      confirmButtonText='Yes, Leave'
      onClose={unsaved.dismissPending}
      onCancel={unsaved.dismissPending}
      onConfirm={unsaved.confirmPending}
    />}
  </>
}
